// Конечный автомат состояний.
//
// Переходы объявлены таблицей: из состояния можно уйти только в перечисленные в ней.
// Незаявленный переход роняет игру сразу, а не оставляет на экране сцену от другого состояния.

/** Таблица переходов: состояние -> состояния, в которые из него можно перейти. */
export type Transitions<TState extends string> = Readonly<Record<TState, readonly TState[]>>;

/** Слушатель перехода. Вызывается после смены состояния. */
export type StateListener<TState extends string> = (state: TState, previous: TState) => void;

/** Автомат состояний с явной таблицей переходов. */
export class StateMachine<TState extends string> {
  readonly #transitions: Transitions<TState>;
  readonly #listeners = new Set<StateListener<TState>>();
  #state: TState;

  constructor(initial: TState, transitions: Transitions<TState>) {
    this.#state = initial;
    this.#transitions = transitions;
  }

  /** Текущее состояние. */
  get state(): TState {
    return this.#state;
  }

  /** Объявлен ли переход из текущего состояния в указанное. */
  canGoTo(next: TState): boolean {
    return this.#transitions[this.#state].includes(next);
  }

  /**
   * Переводит автомат в состояние и оповещает слушателей.
   *
   * @throws Error если такой переход не объявлен
   */
  goTo(next: TState): void {
    if (!this.canGoTo(next)) {
      throw new Error(`Переход ${this.#state} -> ${next} не объявлен`);
    }

    const previous: TState = this.#state;
    this.#state = next;

    for (const listener of this.#listeners) {
      listener(next, previous);
    }
  }

  /** Подписывает слушателя на переходы и возвращает функцию отписки. */
  onChange(listener: StateListener<TState>): () => void {
    this.#listeners.add(listener);

    return () => {
      this.#listeners.delete(listener);
    };
  }
}
